import { MesfModal } from "@dexteel/mesf-core";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import { Alert, Box, Button, Typography } from "@mui/material";
import { Fragment } from "react";
import { capsSx, valueSx } from "@/pages/sawlines/components/primitives";
import { shownTagNo } from "@/pages/sawlines/utils/entryBarcode";
import { TabletConfirm } from "@/pages/saw-tablet/components/TabletConfirm";
import type { MillBundle } from "../repositories/tagStationRepository";
import {
	changed,
	FIELDS,
	type Field,
	type SectionMap,
	sent,
	shown,
	type Values,
} from "../tagStation";
import { fromText, materialText, whenText } from "./BundleCard";
import { TagPreview } from "./TagPreview";

export type FlagKind = "aside" | "unaside" | "scrap" | "unscrap" | "delete";

export const FLAGS: Record<
	FlagKind,
	{
		title: string;
		bar: string;
		effect: string;
		ask: string;
		verb: string;
		color: "success" | "warning" | "error" | "inherit";
		// none: the bundle is deleted
		flags?: { aside?: boolean; scrap?: boolean };
		done: string;
	}
> = {
	aside: {
		title: "SET ASIDE",
		bar: "#e0a100",
		effect:
			"The bundle comes off the transfer. It stays in To print as Set aside and its tag can still be printed from here.",
		ask: "Set this bundle aside?",
		verb: "Set aside",
		color: "warning",
		flags: { aside: true },
		done: "was set aside",
	},
	unaside: {
		title: "BACK ON THE TRANSFER",
		bar: "#9e9e9e",
		effect: "The bundle goes back to To print, without the Set aside mark.",
		ask: "Put this bundle back on the transfer?",
		verb: "Put it back",
		color: "success",
		flags: { aside: false },
		done: "is back on the transfer",
	},
	scrap: {
		title: "MARK AS SCRAP",
		bar: "#c62828",
		effect:
			"The bundle is recorded as scrap and its tag prints with SCRAP on it.",
		ask: "Mark this bundle as scrap?",
		verb: "Mark as scrap",
		color: "error",
		flags: { scrap: true },
		done: "was marked as scrap",
	},
	unscrap: {
		title: "REMOVE SCRAP",
		bar: "#9e9e9e",
		effect:
			"The bundle goes back to good material and its tag prints without SCRAP.",
		ask: "Remove the scrap mark from this bundle?",
		verb: "Remove scrap",
		color: "success",
		flags: { scrap: false },
		done: "is no longer scrap",
	},
	delete: {
		title: "DELETE BUNDLE",
		bar: "#c62828",
		effect: "The bundle is removed from the station for good.",
		ask: "Delete this bundle?",
		verb: "Delete",
		color: "error",
		done: "was deleted",
	},
};

export const FlagConfirm = ({
	kind,
	bundle,
	values,
	onClose,
}: {
	kind: FlagKind;
	bundle: MillBundle;
	values: Values;
	onClose: (confirmed: boolean) => void;
}) => {
	const flag = FLAGS[kind];
	return (
		<TabletConfirm
			title={flag.title}
			message={flag.ask}
			confirmLabel={flag.verb}
			confirmColor={flag.color}
			onClose={(confirmed) => onClose(confirmed === true)}
		>
			<Typography color="text.secondary" sx={{ fontSize: 16, mb: 2 }}>
				{flag.effect}
			</Typography>
			{kind === "delete" && bundle.TagNo && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Tag {shownTagNo(bundle.TagNo)} is already printed. The paper tag stays
					on the bundle and its number is not used again.
				</Alert>
			)}
			<Box
				sx={{
					border: 1,
					borderColor: "divider",
					borderLeft: `6px solid ${flag.bar}`,
					borderRadius: 1,
					px: 2,
					py: 1.5,
					mb: 2.5,
				}}
			>
				<Typography sx={{ ...capsSx, fontSize: 12, mb: 1 }}>Bundle</Typography>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "auto 1fr",
						columnGap: 2.5,
						rowGap: 0.5,
						alignItems: "baseline",
					}}
				>
					{[
						["Material", materialText(values)],
						["Pieces", values.pieces],
						["Heat", values.heat || "—"],
						["From", fromText(bundle)],
						[bundle.PrintedAt ? "Printed" : "Arrived", whenText(bundle)],
						...(bundle.TagNo ? [["Tag", shownTagNo(bundle.TagNo)]] : []),
					].map(([label, value]) => (
						<Fragment key={label}>
							<Typography sx={capsSx}>{label}</Typography>
							<Typography sx={valueSx}>{value}</Typography>
						</Fragment>
					))}
				</Box>
			</Box>
		</TabletConfirm>
	);
};

const BundleData = ({
	arrived,
	values,
}: {
	arrived: Values;
	values: Values;
}) => {
	const diff = changed(arrived, values);
	return (
		<Box
			sx={{
				display: "flex",
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				my: 2.25,
			}}
		>
			{FIELDS.map(([f, name], index) => {
				const corrected = diff.includes(f);
				return (
					<Box
						key={f}
						sx={{
							flex: "1 1 auto",
							minWidth: 0,
							px: 2,
							py: 1.1,
							borderLeft: index ? 1 : 0,
							borderColor: "divider",
						}}
					>
						<Typography
							sx={{
								...capsSx,
								fontSize: 10.5,
								mb: 0.4,
								...(corrected && { color: "text.primary" }),
							}}
						>
							{name}
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "baseline",
								gap: 0.9,
								fontSize: "1.05rem",
								whiteSpace: "nowrap",
								fontVariantNumeric: "tabular-nums",
								color: "text.secondary",
							}}
						>
							{corrected && (
								<>
									<span>{shown(arrived, f) || "—"}</span>
									<span>→</span>
								</>
							)}
							<Box
								component="span"
								sx={
									corrected
										? {
												bgcolor: "#FFE58A",
												color: "#111",
												fontWeight: 700,
												px: 0.9,
												borderRadius: 0.75,
											}
										: undefined
								}
							>
								{shown(values, f) || "—"}
							</Box>
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};

const listFormat = new Intl.ListFormat("en", { type: "conjunction" });

// true = print, "edit" = open the bundle in the checklist, false = cancel
export const PrintConfirm = ({
	bundle,
	values,
	sections,
	missing,
	opened,
	nextTagNo,
	onClose,
}: {
	bundle: MillBundle;
	values: Values;
	sections: SectionMap;
	// shown on the tag until the bundle has its own
	nextTagNo: string | null;
	// values not confirmed yet; always empty on a reprint
	missing: Field[];
	// already in the checklist: nothing to open
	opened: boolean;
	onClose: (result: boolean | "edit") => void;
}) => {
	const reprint = bundle.State === "Printed";
	const names = FIELDS.filter(([f]) => missing.includes(f)).map(([, n]) => n);
	const them = names.length > 1 ? "them" : "it";
	return (
		<MesfModal
			title={reprint ? "REPRINT" : "SAVE AND PRINT"}
			open
			maxWidth="md"
			handleClose={() => onClose(false)}
		>
			{/* wider than MesfModal's 600: the tag reads at 600 */}
			<MesfModal.Content
				dividers
				style={{ padding: "20px 30px 22px", minWidth: 720 }}
			>
				<TagPreview
					values={values}
					tagNo={bundle.TagNo ?? nextTagNo}
					date={bundle.PrintedAt}
					scrap={bundle.IsScrap}
					sections={sections}
					base={reprint ? undefined : sent(bundle)}
					baseLabel={bundle.IsManual ? "Entered" : "Mill sent"}
					width="600px"
				/>
				<BundleData arrived={sent(bundle)} values={values} />
				{names.length > 0 ? (
					<Alert severity="warning">
						{listFormat.format(names)} {names.length > 1 ? "are" : "is"} not
						confirmed yet.{" "}
						{opened
							? `Confirm ${them} in the checklist.`
							: `Open the bundle to check ${them}.`}
					</Alert>
				) : (
					<Typography sx={{ fontSize: 20, fontWeight: 600 }}>
						{reprint
							? `Reprint tag ${bundle.TagNo && shownTagNo(bundle.TagNo)}?`
							: "Save and print this tag?"}
					</Typography>
				)}
			</MesfModal.Content>
			<MesfModal.Actions style={{ padding: "14px 30px", gap: 12 }}>
				<Button
					variant="outlined"
					color="inherit"
					onClick={() => onClose(false)}
				>
					Cancel
				</Button>
				{!reprint && !opened && (
					<Button
						variant={names.length > 0 ? "contained" : "outlined"}
						startIcon={<EditIcon />}
						onClick={() => onClose("edit")}
					>
						Edit
					</Button>
				)}
				<Button
					variant="contained"
					startIcon={<PrintIcon />}
					disabled={names.length > 0}
					onClick={() => onClose(true)}
				>
					{reprint ? "Reprint" : "Save and print"}
				</Button>
			</MesfModal.Actions>
		</MesfModal>
	);
};
